import {GuiaCamara} from './carga-documento'

/**
 * CÁMARA CON RECUADRO (Omnicanal → «Cómo se sube» → guía de la cámara)
 * =====================================================================
 * Con `settings.guia` la liga abre su propia cámara (getUserMedia) con un
 * recuadro encima para que el documento quede centrado, y sube SOLO lo que quedó
 * dentro del recuadro (más un margen chico). Sin guía, «Tomar foto» abre la
 * cámara del celular como siempre (input con capture).
 *
 * El video se pinta con `object-fit: cover`: llena la pantalla y se recorta por
 * los lados, así que el recuadro (en píxeles de pantalla) se pasa a píxeles del
 * video con la misma escala y el mismo centrado que usa el navegador.
 */

export interface IRect {
  x: number
  y: number
  ancho: number
  alto: number
}

/**
 * Recuadros del visor: los de Omnicanal (settings.guia) y el del pasaporte, que
 * no se configura: sale de la identificación elegida en un documento de
 * identidad (guiaDeIdentificacion en identificaciones.ts). GuiaCamara se queda
 * como está porque es la misma regla del panel.
 */
export type RecuadroCamara = GuiaCamara | 'PASAPORTE'

/**
 * Ancho entre alto del recuadro: credencial ID-1 (85.6 × 53.98 mm), hoja carta
 * (21.59 × 27.94 cm) y hoja de datos del pasaporte ID-3 (125 × 88 mm, horizontal).
 */
export const PROPORCION_GUIA: Record<RecuadroCamara, number> = {
  TARJETA: 85.6 / 53.98,
  HOJA: 21.59 / 27.94,
  PASAPORTE: 125 / 88,
}

/** Margen alrededor del recuadro, sobre su lado corto: que no se corte el borde del documento. */
export const MARGEN_RECORTE = 0.05
/** Lado largo máximo de la foto que se sube (se lee bien y pesa ~1 MB en JPG). */
export const LADO_MAXIMO_FOTO = 2400
export const CALIDAD_JPG = 0.92

/** Lo que pide la liga a la cámara: la trasera y en buena resolución (son ideales, no obligatorios). */
export const RESTRICCIONES_CAMARA: MediaStreamConstraints = {
  audio: false,
  video: {facingMode: {ideal: 'environment'}, width: {ideal: 1920}, height: {ideal: 1080}},
}

/**
 * El recuadro de pantalla (`marco`) en píxeles del video, con margen y sin
 * salirse del cuadro. `elemento` es la caja del <video> en pantalla. null si
 * algo no tiene tamaño (el video aún no carga) o el recuadro cae fuera.
 */
export function recorteDelMarco(
  video: {ancho: number; alto: number},
  elemento: IRect,
  marco: IRect,
  margen = MARGEN_RECORTE
): IRect | null {
  const positivos = [video.ancho, video.alto, elemento.ancho, elemento.alto, marco.ancho, marco.alto]
  if (!positivos.every((n) => Number.isFinite(n) && n > 0)) return null
  // object-fit: cover — se escala hasta cubrir la caja y se centra.
  const escala = Math.max(elemento.ancho / video.ancho, elemento.alto / video.alto)
  const dx = (elemento.ancho - video.ancho * escala) / 2
  const dy = (elemento.alto - video.alto * escala) / 2
  const m = Math.min(marco.ancho, marco.alto) * margen
  const aVideo = (pantalla: number, origen: number, desplazamiento: number) => (pantalla - origen - desplazamiento) / escala
  const x0 = Math.max(0, aVideo(marco.x - m, elemento.x, dx))
  const y0 = Math.max(0, aVideo(marco.y - m, elemento.y, dy))
  const x1 = Math.min(video.ancho, aVideo(marco.x + marco.ancho + m, elemento.x, dx))
  const y1 = Math.min(video.alto, aVideo(marco.y + marco.alto + m, elemento.y, dy))
  const recorte = {x: Math.round(x0), y: Math.round(y0), ancho: Math.round(x1 - x0), alto: Math.round(y1 - y0)}
  return recorte.ancho > 0 && recorte.alto > 0 ? recorte : null
}

/** Tamaño de la foto final: el del recorte, sin pasar de LADO_MAXIMO_FOTO en el lado largo. */
export function tamanoDeSalida(recorte: Pick<IRect, 'ancho' | 'alto'>, ladoMaximo = LADO_MAXIMO_FOTO): {ancho: number; alto: number} {
  const factor = Math.min(1, ladoMaximo / Math.max(recorte.ancho, recorte.alto))
  return {ancho: Math.max(1, Math.round(recorte.ancho * factor)), alto: Math.max(1, Math.round(recorte.alto * factor))}
}

function enNombre(texto: string): string {
  return (texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** «ine-anverso.jpg»: con lo que el colaborador reconoce (documento y cara), sin acentos ni espacios. */
export function nombreDeFoto(titulo: string, etiqueta: string, tipo: string): string {
  const base = [enNombre(titulo), enNombre(etiqueta)].filter(Boolean).join('-') || 'foto'
  return `${base}.${tipo === 'image/png' ? 'png' : 'jpg'}`
}

/** Por qué no abrió la cámara, en palabras del colaborador (nombres de error de getUserMedia). */
export function motivoSinCamara(error: unknown): string {
  const nombre = (error as {name?: string} | null)?.name ?? ''
  if (nombre === 'NotAllowedError' || nombre === 'SecurityError') {
    return 'No se dio permiso para usar la cámara. Puedes activarlo en los ajustes del navegador o tomar la foto sin recuadro.'
  }
  if (nombre === 'NotFoundError' || nombre === 'OverconstrainedError') return 'No encontramos una cámara en este equipo.'
  if (nombre === 'NotReadableError' || nombre === 'AbortError') return 'Otra app está usando la cámara. Ciérrala e intenta de nuevo, o toma la foto sin recuadro.'
  return 'No pudimos abrir la cámara aquí.'
}

/** Lo que se le pide al colaborador debajo del recuadro. */
export function instruccionDeGuia(guia: RecuadroCamara): {texto: string; consejo: string} {
  if (guia === 'HOJA') return {texto: 'Acomoda la hoja completa dentro del recuadro', consejo: 'Sobre una mesa, con buena luz y sin sombras.'}
  if (guia === 'PASAPORTE') {
    return {texto: 'Acomoda la hoja de datos de tu pasaporte dentro del recuadro', consejo: 'Ábrelo en la hoja de tu foto, sobre una superficie lisa y sin reflejos.'}
  }
  return {texto: 'Acomoda tu identificación dentro del recuadro', consejo: 'Sobre una superficie lisa, con buena luz y sin reflejos.'}
}

// =============================================================================
// FOTO AUTOMÁTICA AL ENFOCAR
// -----------------------------------------------------------------------------
// Unas 6–7 veces por segundo se toma lo que está DENTRO del recuadro, reducido a
// ANCHO_ANALISIS px en grises, y se mide:
//   nitidez     varianza del laplaciano (bordes marcados = número alto; borroso = bajo)
//   movimiento  diferencia media contra la muestra anterior (0–255)
//   brillo      gris medio (0–255)
// Se dispara sola cuando hay luz, no se mueve, la nitidez pasa el mínimo y está
// cerca de la mejor que se ha visto en los últimos segundos (el enfoque del
// celular sube y baja: se toma en el pico), durante MUESTRAS_SEGUIDAS muestras.
// Si en ESPERA_MANUAL_MS no lo logra, se habilita el disparador para tomarla a mano.
// Los umbrales son de calibración: con `camaraDebug` en el environment (el del
// túnel lo trae) el visor muestra los números para ajustarlos con un celular real.
// =============================================================================

export type LecturaEnfoque = 'oscuro' | 'moviendo' | 'enfocando' | 'quieto' | 'lista'

export interface IConfigEnfoque {
  /** Varianza del laplaciano mínima (a ANCHO_ANALISIS px). */
  nitidezMinima: number
  /** Qué tan cerca del mejor enfoque reciente debe estar (0–1). */
  proporcionDelMaximo: number
  /** Cuánto se olvida el mejor enfoque por muestra (el máximo baja si el documento se aleja). */
  decaimientoMaximo: number
  /** Diferencia media entre muestras para considerarla quieta. */
  movimientoMaximo: number
  /** Gris medio mínimo. */
  brilloMinimo: number
  /** Muestras buenas seguidas para disparar. */
  muestrasSeguidas: number
  /** Al abrir, la cámara ajusta exposición y enfoque: no se dispara antes. */
  calentamientoMs: number
}

export const CONFIG_ENFOQUE: IConfigEnfoque = {
  nitidezMinima: 80,
  proporcionDelMaximo: 0.82,
  decaimientoMaximo: 0.97,
  movimientoMaximo: 7,
  brilloMinimo: 45,
  muestrasSeguidas: 4,
  calentamientoMs: 900,
}

export const ESPERA_MANUAL_MS = 5000
export const INTERVALO_ANALISIS_MS = 150
export const ANCHO_ANALISIS = 400

export interface IMuestraEnfoque {
  nitidez: number
  brillo: number
  /** Grises de la muestra (para medir el movimiento contra la siguiente). */
  gris: Uint8Array
}

export interface ISeguimientoEnfoque {
  maximo: number
  racha: number
  anterior: Uint8Array | null
}

export const seguimientoInicial = (): ISeguimientoEnfoque => ({maximo: 0, racha: 0, anterior: null})

/** RGBA (getImageData) a grises con los pesos de luminancia de siempre. */
export function aGrises(rgba: ArrayLike<number>): Uint8Array {
  const gris = new Uint8Array(Math.floor(rgba.length / 4))
  for (let i = 0, j = 0; j < gris.length; i += 4, j++) {
    gris[j] = (rgba[i] * 299 + rgba[i + 1] * 587 + rgba[i + 2] * 114) / 1000
  }
  return gris
}

/** Varianza del laplaciano (4 vecinos) sin el borde de 1 px. 0 en una imagen lisa. */
export function varianzaLaplaciano(gris: ArrayLike<number>, ancho: number, alto: number): number {
  if (ancho < 3 || alto < 3) return 0
  let suma = 0
  let sumaCuadrados = 0
  let n = 0
  for (let y = 1; y < alto - 1; y++) {
    const fila = y * ancho
    for (let x = 1; x < ancho - 1; x++) {
      const i = fila + x
      const l = 4 * gris[i] - gris[i - 1] - gris[i + 1] - gris[i - ancho] - gris[i + ancho]
      suma += l
      sumaCuadrados += l * l
      n++
    }
  }
  const media = suma / n
  return sumaCuadrados / n - media * media
}

export function brilloMedio(gris: ArrayLike<number>): number {
  if (!gris.length) return 0
  let suma = 0
  for (let i = 0; i < gris.length; i++) suma += gris[i]
  return suma / gris.length
}

/** Diferencia media absoluta entre dos muestras del mismo tamaño (null si no se pueden comparar). */
export function diferenciaMedia(a: ArrayLike<number> | null, b: ArrayLike<number>): number | null {
  if (!a || a.length !== b.length || !b.length) return null
  let suma = 0
  for (let i = 0; i < b.length; i++) suma += Math.abs(a[i] - b[i])
  return suma / b.length
}

/** Una muestra más: cómo va el enfoque y si ya toca disparar ('lista'). Puro. */
export function evaluarMuestra(
  seguimiento: ISeguimientoEnfoque,
  muestra: IMuestraEnfoque,
  transcurridoMs: number,
  config: IConfigEnfoque = CONFIG_ENFOQUE
): {seguimiento: ISeguimientoEnfoque; lectura: LecturaEnfoque; progreso: number; movimiento: number | null} {
  const movimiento = diferenciaMedia(seguimiento.anterior, muestra.gris)
  const maximo = Math.max(muestra.nitidez, seguimiento.maximo * config.decaimientoMaximo)
  let lectura: LecturaEnfoque
  if (muestra.brillo < config.brilloMinimo) lectura = 'oscuro'
  else if (movimiento === null || movimiento > config.movimientoMaximo) lectura = 'moviendo'
  else if (muestra.nitidez < config.nitidezMinima || muestra.nitidez < maximo * config.proporcionDelMaximo) lectura = 'enfocando'
  else lectura = 'quieto'
  const racha = lectura === 'quieto' && transcurridoMs >= config.calentamientoMs ? seguimiento.racha + 1 : 0
  if (racha >= config.muestrasSeguidas) lectura = 'lista'
  return {
    seguimiento: {maximo, racha, anterior: muestra.gris},
    lectura,
    progreso: Math.min(1, racha / config.muestrasSeguidas),
    movimiento,
  }
}

/** Lo que dice el visor mientras busca el enfoque. */
export const TEXTO_LECTURA: Record<LecturaEnfoque, string> = {
  oscuro: 'Falta luz: busca un lugar más iluminado',
  moviendo: 'Mantén el celular quieto',
  enfocando: 'Enfocando…',
  quieto: 'No te muevas…',
  lista: '¡Listo!',
}
